import type { useIntl } from 'react-intl';
import type { z } from 'zod';
import {
  MAX_ACTIVITY_DESCRIPTION_LENGTH,
  MIN_ACTIVITY_DESCRIPTION_LENGTH,
} from '#organizations/constants/business-activity';
import { DEFAULT_ZIPCODE_REGEX, FIVE_DIGITS_COUNTRIES, FIVE_DIGITS_REGEX } from './primitives';

type FormatMessage = ReturnType<typeof useIntl>['formatMessage'];

/**
 * Country-aware zipcode pattern check. Mirrors `validateZipcode` from the
 * existing form validations 1:1.
 */
export function addZipcodeRefinement(
  zipcode: string | undefined,
  country: string | null | undefined,
  path: string[],
  ctx: z.RefinementCtx,
  formatMessage: FormatMessage,
  { isRequired = false }: { isRequired?: boolean } = {}
): void {
  if (!zipcode || zipcode.trim() === '') {
    if (isRequired) {
      ctx.addIssue({
        code: 'custom',
        message: formatMessage({ id: 'customer-registration-kit.validation.zipcode.required' }),
        path,
      });
    }
    return;
  }

  // oxlint-disable-next-line typescript/consistent-type-assertions
  const isFiveDigits = country && (FIVE_DIGITS_COUNTRIES as readonly string[]).includes(country);
  const regex = isFiveDigits ? FIVE_DIGITS_REGEX : DEFAULT_ZIPCODE_REGEX;

  if (!regex.test(zipcode)) {
    ctx.addIssue({
      code: 'custom',
      message: formatMessage(
        { id: 'customer-registration-kit.validation.zipcode.pattern' },
        { isFiveDigits: Boolean(isFiveDigits) }
      ),
      path,
    });
  }
}

/**
 * Adds the generic "required field" issue for a string-array field when it is
 * required and empty. Shared by the activity-form schemas (IT/BE and ES) so the
 * same emptiness check isn't hand-rolled per schema.
 */
export function addRequiredArrayRefinement(
  ctx: z.RefinementCtx,
  path: string,
  values: readonly string[] | undefined,
  formatMessage: (descriptor: { id: string }) => string,
  isRequired = true
): void {
  if (isRequired && !values?.length) {
    ctx.addIssue({
      code: 'custom',
      message: formatMessage({ id: 'generic.required-field.error' }),
      path: [path],
    });
  }
}

/**
 * Adds the activity-description issues (required / too-short / too-long) shared by
 * the ES and IT/BE activity-form schemas. Length bounds come from the unified
 * `MIN`/`MAX_ACTIVITY_DESCRIPTION_LENGTH` constants. The minimum uses trimmed length so
 * persisted descriptions meet the requirement; the maximum uses raw length to match the
 * character counter. Whitespace-only input is rejected.
 */
export function addActivityDescriptionRefinement(
  ctx: z.RefinementCtx,
  value: string | undefined,
  formatMessage: (descriptor: { id: string }) => string
): void {
  const activityDescription = value ?? '';

  if (!activityDescription.trim()) {
    ctx.addIssue({
      code: 'custom',
      message: formatMessage({
        id: 'customer-registration-kit.activity-description.error-message',
      }),
      path: ['activityDescription'],
    });
  } else if (activityDescription.trim().length < MIN_ACTIVITY_DESCRIPTION_LENGTH) {
    ctx.addIssue({
      code: 'custom',
      message: formatMessage({
        id: 'customer-registration-kit.activity-description.error-message-short-input',
      }),
      path: ['activityDescription'],
    });
  } else if (activityDescription.length > MAX_ACTIVITY_DESCRIPTION_LENGTH) {
    ctx.addIssue({
      code: 'custom',
      message: formatMessage({
        id: 'customer-registration-kit.activity-description.error-message-long-input',
      }),
      path: ['activityDescription'],
    });
  }
}
